import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { DdmStream } from '@bloo-team/api-client'
import { DEMO_DDM as CORE_DEMO_DDM, getSession, identityFor, isDemoSession } from '@bloo-team/core-shell'
import { dataModelStore, fieldKey, useDataModelOverlayState } from './data-model-store'
import { ddmApi } from './extractor-detail'
import { demoDetailFor } from './extractor-detail-demo'
import { extractorOverlayStore } from './extractor-store'
import { parseOutline } from './extractor-yaml'
import { extractorsApi, useExtractors, type Extractor } from './extractors'

/**
 * Data Model data — the stream → field catalog (docs/data-model-requirements.md).
 *
 * Reads: `ddm/list` (captured; `ddmApi` is the instance the Extractors stages
 * share). Demo: core-shell's `DEMO_DDM` with the overlay's `added` rows
 * applied, so a demo add sticks across reloads.
 * Writes: `POST /api/ddm` is declared-only until capture D1, so live mode
 * renders Add field / Add stream DISABLED with `gateReason('ddm')`
 * (`WRITE_GATES`). Demo writes the localStorage overlay (`data-model-store.ts`).
 *
 * The query key shares its `['ddm', clusterId, scopeId]` prefix with
 * `useDdm` (extractor-detail.ts), so one invalidation refreshes both readers.
 */

export type { DdmStream }
export { ddmApi }

/* ------------------------------------------------------------------ gates */

/** Capture gate per write (docs/data-model-requirements.md §Capture ledger). */
export const WRITE_GATES = { ddm: 'D1' } as const

export type WriteGate = keyof typeof WRITE_GATES

const GATE_ENDPOINT: Record<WriteGate, string> = { ddm: 'POST /api/ddm' }

/** The disabled button's title / the muted line beside the form. */
export function gateReason(gate: WriteGate): string {
  return `Write shape unconfirmed — capture ${WRITE_GATES[gate]} (${GATE_ENDPOINT[gate]}) pending (docs/data-model-requirements.md §Capture ledger)`
}

/**
 * Who may write. Legacy gated `streamfieldaddition` to Content manager — the
 * only routes with that role — and the product owner confirmed 2026-09-09 that
 * it is Content Manager ONLY (Root does not author the model; an Organization
 * Admin got 'user not authorised' on D1, inventory/storage-capture.json). A UX
 * affordance; authorization is the server's (permissions.ts). Read is open to
 * the analyst-or-engineer roles like the rest of Datafabric.
 */
export function canWriteDataModel(clusterId: string, scopeId: string): boolean {
  const session = getSession()
  if (!session) return false
  const { userType } = identityFor(session, clusterId, scopeId)
  return userType === 'ContentManager'
}

/* ------------------------------------------------------------------ reads */

interface Scope {
  clusterId: string
  scopeId: string
}

const rowsKey = ({ clusterId, scopeId }: Scope) =>
  ['ddm', clusterId, scopeId, isDemoSession() ? 'demo' : 'live', 'rows'] as const

/**
 * Overlay `added` rows applied over the base rows: a row whose stream the base
 * already has REPLACES it in place (the fixture row's copy with the added
 * field); a new stream is appended. Untouched rows are the same object.
 */
export function applyDataModelOverlay(rows: DdmStream[], added: DdmStream[]): DdmStream[] {
  if (added.length === 0) return rows
  const byStream = new Map(added.map((r) => [r.stream, r]))
  const out = rows.map((r) => byStream.get(r.stream) ?? r)
  const base = new Set(rows.map((r) => r.stream))
  for (const r of added) if (!base.has(r.stream)) out.push(r)
  return out
}

/** Every stream in the model. Demo: fixtures + the overlay's added rows. */
export function useDataModel({ clusterId, scopeId }: Scope) {
  const isDemo = isDemoSession()
  const overlay = useDataModelOverlayState(clusterId, scopeId)
  const q = useQuery<DdmStream[]>({
    queryKey: rowsKey({ clusterId, scopeId }),
    queryFn: async () => (isDemo ? CORE_DEMO_DDM : ddmApi.list({ clusterId, scopeId })),
    staleTime: 300_000,
    retry: 1,
  })
  const data = useMemo<DdmStream[] | undefined>(() => {
    if (!q.data || !isDemo) return q.data
    return applyDataModelOverlay(q.data, overlay.added)
  }, [q.data, isDemo, overlay.added])
  return { ...q, data }
}

/* ------------------------------------------------------------------ index */

/** field → the streams that declare it, each list sorted, one entry per stream. */
export function fieldIndex(rows: DdmStream[]): Map<string, string[]> {
  const sets = new Map<string, Set<string>>()
  for (const r of rows) {
    for (const f of r.fields) {
      let s = sets.get(f)
      if (!s) sets.set(f, (s = new Set()))
      s.add(r.stream)
    }
  }
  const out = new Map<string, string[]>()
  for (const [f, s] of sets) out.set(f, [...s].sort((a, b) => a.localeCompare(b)))
  return out
}

export interface FieldHit {
  field: string
  streams: string[]
}

/** Case-insensitive substring over the field names, sorted by field. An empty query matches every field. */
export function searchFields(index: Map<string, string[]>, q: string): FieldHit[] {
  const needle = q.trim().toLowerCase()
  const out: FieldHit[] = []
  for (const [field, streams] of index) {
    if (needle === '' || field.toLowerCase().includes(needle)) out.push({ field, streams })
  }
  return out.sort((a, b) => a.field.localeCompare(b.field))
}

export interface StreamFact {
  fieldCount: number
  /** stream-management reports the stream — bytes exist for it now. */
  hasData: boolean
  /** `created_by` is not the stock author `DNIF` (a missing author counts as custom). */
  custom: boolean
  /** Epoch ms from `update_tstamp` (else `create_tstamp`); null when neither parses. */
  updatedAt: number | null
}

export const STOCK_AUTHOR = 'DNIF'

/** An epoch-second string (`'1787833855'`, `'1787833855.1'`) → epoch ms, or null. */
export function epochSecondsToMs(v: string | null | undefined): number | null {
  if (v === null || v === undefined || v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? Math.round(n * 1000) : null
}

/** The left pane's row facts and the strip's counts, per stream. */
export function streamFacts(rows: DdmStream[], liveStreamNames: string[]): Map<string, StreamFact> {
  const live = new Set(liveStreamNames)
  const out = new Map<string, StreamFact>()
  for (const r of rows) {
    out.set(r.stream, {
      fieldCount: r.fields.length,
      hasData: live.has(r.stream),
      custom: (r.created_by ?? '') !== STOCK_AUTHOR,
      updatedAt: epochSecondsToMs(r.update_tstamp) ?? epochSecondsToMs(r.create_tstamp),
    })
  }
  return out
}

/* ----------------------------------------------------------------- writes */

export interface AddDdmInput {
  stream: string
  /** Omitted: declare a new stream. Given: add the field to `stream`. */
  field?: string
}

const nowSeconds = () => String(Math.floor(Date.now() / 1000))

/** The demo echo of `POST /api/ddm` — the row the overlay keeps. */
export function demoAddRow(current: DdmStream[], input: AddDdmInput, author: string, scopeId: string): DdmStream {
  const stream = input.stream.trim()
  if (!stream) throw new Error('A stream name is required')
  const field = input.field?.trim()
  const existing = current.find((r) => r.stream === stream)
  const at = nowSeconds()
  if (field === undefined) {
    if (existing) throw new Error(`${stream} is already in the model`)
    return {
      stream, stream_id: `${stream}_${scopeId}`, fields: [], scope_id: scopeId,
      created_by: author, updated_by: author, create_tstamp: at, update_tstamp: at,
    }
  }
  if (!field) throw new Error('A field name is required')
  if (existing) {
    if (existing.fields.includes(field)) throw new Error(`${stream} already declares ${field}`)
    return { ...existing, fields: [...existing.fields, field], updated_by: author, update_tstamp: at }
  }
  // One endpoint, two intents: a field on an unknown stream declares the stream with it.
  return {
    stream, stream_id: `${stream}_${scopeId}`, fields: [field], scope_id: scopeId,
    created_by: author, updated_by: author, create_tstamp: at, update_tstamp: at,
  }
}

/**
 * Add a stream, or a field to a stream. Demo → the overlay (`added`); live →
 * `POST /api/ddm` then every `['ddm', clusterId, scopeId]` reader refetches
 * (this page and the Extractors stages' `useDdm`).
 */
export function useAddDdm(scope: Scope) {
  const qc = useQueryClient()
  return useMutation<void, Error, AddDdmInput>({
    mutationFn: async (input) => {
      if (isDemoSession()) {
        const store = dataModelStore(scope.clusterId, scope.scopeId)
        const current = applyDataModelOverlay(CORE_DEMO_DDM, store.all().added)
        const author = getSession()?.user.name ?? 'demo'
        store.addRow(demoAddRow(current, input, author, scope.scopeId))
        return
      }
      await ddmApi.add({ ...scope, stream: input.stream.trim(), ...(input.field !== undefined ? { field: input.field.trim() } : {}) })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ddm', scope.clusterId, scope.scopeId] }),
  })
}

/* ------------------------------------------------- reverse extractor index */

export interface ExtractorText {
  extractorId: string
  name: string
  yaml: string
}

/**
 * The per-extractor YAML cache key — IDENTICAL to the conflict check's
 * (extractor-verify.ts `useConflictCheck`), so the two share one five-minute
 * cache entry per extractor instead of loading `listparserdetails` twice.
 */
export const extractorTextKey = (clusterId: string, scopeId: string, extractorId: string) =>
  ['extractor-conflict-yaml', clusterId, scopeId, extractorId, isDemoSession() ? 'demo' : 'live'] as const

export const EXTRACTOR_TEXT_STALE_MS = 5 * 60_000

/** One extractor's published YAML: the overlay's published text, else the fixture (demo) / `listparserdetails` (live). */
export async function loadExtractorText(clusterId: string, scopeId: string, row: Extractor): Promise<string> {
  if (isDemoSession()) {
    const pub = extractorOverlayStore(clusterId, scopeId).all()[row.extractor_id]?.published
    return pub ? pub.yaml : demoDetailFor(row.extractor_id, row.name, row.source_name ?? null).yaml
  }
  return (await extractorsApi.details({ clusterId, scopeId, extractorId: row.extractor_id })).yaml
}

/** Published catalogue rows only — drafts (`<id>_draft`) are not what the tenant runs. */
export function indexableExtractors(rows: Extractor[]): Extractor[] {
  return rows.filter((r) => !/_draft$/.test(r.extractor_id))
}

/**
 * Every indexable extractor's YAML through the shared cache. A row whose
 * text fails to load is skipped rather than failing the whole set.
 */
export async function fetchExtractorTexts(qc: QueryClient, { clusterId, scopeId }: Scope, rows: Extractor[]): Promise<ExtractorText[]> {
  const settled = await Promise.allSettled(
    indexableExtractors(rows).map(async (row) => ({
      extractorId: row.extractor_id,
      name: row.name,
      yaml: await qc.fetchQuery({
        queryKey: extractorTextKey(clusterId, scopeId, row.extractor_id),
        queryFn: () => loadExtractorText(clusterId, scopeId, row),
        staleTime: EXTRACTOR_TEXT_STALE_MS,
        retry: false,
      }),
    })),
  )
  return settled.filter((s): s is PromiseFulfilledResult<ExtractorText> => s.status === 'fulfilled').map((s) => s.value)
}

/**
 * The catalogue's YAML texts as one query. `enabled` (default true) plus a
 * loaded, non-empty catalogue gate the load; the per-extractor entries are
 * the five-minute cache the conflict check also reads.
 */
export function useExtractorTexts({ clusterId, scopeId, enabled = true }: Scope & { enabled?: boolean }) {
  const qc = useQueryClient()
  const list = useExtractors({ clusterId, scopeId })
  const rows = useMemo(() => indexableExtractors(list.data?.extractors ?? []), [list.data])
  const ids = rows.map((r) => r.extractor_id).join(',')
  return useQuery<ExtractorText[]>({
    queryKey: ['extractor-texts', clusterId, scopeId, isDemoSession() ? 'demo' : 'live', ids],
    enabled: enabled && rows.length > 0,
    staleTime: EXTRACTOR_TEXT_STALE_MS,
    retry: false,
    queryFn: () => fetchExtractorTexts(qc, { clusterId, scopeId }, rows),
  })
}

/** The (stream, field) pairs one extractor's YAML writes: translate targets and annotated constants per key-mapping (never `Stream` itself, never the OTHER fallback). */
export function writtenFields(yaml: string): Array<[stream: string, field: string]> {
  let outline: ReturnType<typeof parseOutline>
  try {
    outline = parseOutline(yaml)
  } catch {
    return []
  }
  const seen = new Set<string>()
  const out: Array<[string, string]> = []
  const add = (stream: string, field: string) => {
    if (!field || field === 'Stream') return
    const k = fieldKey(stream, field)
    if (seen.has(k)) return
    seen.add(k)
    out.push([stream, field])
  }
  for (const e of outline.events) {
    const globals = e.globals ? Object.values(e.globals.translate) : []
    for (const m of e.mappings) {
      const stream = m.annotate.Stream
      if (!stream) continue
      for (const f of Object.values(m.translate)) add(stream, f)
      for (const f of Object.keys(m.annotate)) add(stream, f)
      for (const f of globals) add(stream, f)
    }
  }
  return out
}

/** `"<stream>/<field>"` → the extractor ids that write it, sorted. Pure — the hook below feeds it. */
export function buildFieldExtractorIndex(texts: ExtractorText[]): Map<string, string[]> {
  const sets = new Map<string, Set<string>>()
  for (const t of texts) {
    for (const [stream, field] of writtenFields(t.yaml)) {
      const k = fieldKey(stream, field)
      let s = sets.get(k)
      if (!s) sets.set(k, (s = new Set()))
      s.add(t.extractorId)
    }
  }
  const out = new Map<string, string[]>()
  for (const [k, s] of sets) out.set(k, [...s].sort((a, b) => a.localeCompare(b)))
  return out
}

/**
 * Which extractors write each field — the field table's "extractors" count
 * and the `/fabric/extractors?field=<S>/<field>` pivot. Lazy: nothing loads
 * until the model `rows` are in (the table has nothing to annotate before
 * then); `data` stays undefined until every text is loaded, so the table
 * never shows a zero that is really "still loading".
 */
export function useFieldExtractorIndex({ clusterId, scopeId, rows }: Scope & { rows: DdmStream[] }) {
  const texts = useExtractorTexts({ clusterId, scopeId, enabled: rows.length > 0 })
  const data = useMemo(() => (texts.data ? buildFieldExtractorIndex(texts.data) : undefined), [texts.data])
  return { ...texts, data }
}
